import { useEffect, useState } from 'react';
import { Link } from 'react-router';

import { ArrowRightIcon, ArrowUpRightIcon, CheckIcon, CopyIcon, LinkIcon, PlugIcon, ShieldCheckIcon } from '@/components/Icons';
import { Page, README_URL } from '@/components/Layout';
import ui from '@/components/ui.module.css';
import { useToast } from '@/components/Toast';
import { publicOrigin } from '@/lib/app';
import { copyText } from '@/lib/clipboard';
import { t, tr, useLocale, type MessageKey } from '@/i18n';

import embedDark from '@/assets/embed-dark.webp';
import embedLight from '@/assets/embed-light.webp';

import styles from './DevelopersPage.module.css';
import { SAMPLES, type Lang } from './developersSamples';

function origin(): string {
  return typeof window === 'undefined' ? 'https://bridge.example.com' : publicOrigin();
}

const LANGS: readonly { id: Lang; label: string }[] = [
  { id: 'curl', label: 'cURL' },
  { id: 'python', label: 'Python' },
  { id: 'node', label: 'Node.js' },
];

/** What an endpoint takes: code that stays as it is, or a sentence from the catalogue. */
type Takes = { readonly code: string } | { readonly key: MessageKey; readonly params?: Record<string, string> };
/** The endpoints, in three groups so each fits the window: method, path, what it takes, what it does. */
interface Endpoint {
  readonly method: 'GET' | 'POST' | 'PUT' | 'DELETE';
  readonly path: string;
  readonly takes: Takes;
  readonly does: MessageKey;
  readonly doesParams?: Record<string, string>;
}
const ENDPOINT_GROUPS: readonly { id: string; label: MessageKey; items: readonly Endpoint[] }[] = [
  {
    id: 'bridges',
    label: 'developers.groupBridges',
    items: [
      { method: 'POST', path: '/api/bridges', takes: { code: '{ ttlSeconds, maxViews, mode? }' }, does: 'developers.epCreate' },
      { method: 'GET', path: '/api/bridges/:id', takes: { key: 'developers.takesOwnerOrClaim' }, does: 'developers.epGet' },
      { method: 'POST', path: '/api/bridges/:id/claims', takes: { key: 'developers.takesNone' }, does: 'developers.epClaim' },
      { method: 'GET', path: '/api/bridges/:id/events', takes: { key: 'developers.takesOwnerOrClaim' }, does: 'developers.epEvents' },
      { method: 'DELETE', path: '/api/bridges/:id', takes: { key: 'developers.takesOwner' }, does: 'developers.epDelete' },
      { method: 'GET', path: '/api/health', takes: { key: 'developers.takesNone' }, does: 'developers.epHealth', doesParams: { result: '{ ok: true }' } },
    ],
  },
  {
    id: 'messages',
    label: 'developers.groupMessages',
    items: [
      { method: 'POST', path: '/api/bridges/:id/messages', takes: { key: 'developers.takesTextOrFile', params: { text: '{ kind: "text", body }', file: '{ kind: "file", header, upload }' } }, does: 'developers.epPost' },
      { method: 'POST', path: '/api/bridges/:id/uploads', takes: { key: 'developers.takesToken', params: { body: '{ chunks, size }' } }, does: 'developers.epUpload' },
      { method: 'PUT', path: '/api/bridges/:id/uploads/:uploadId/chunks/:index', takes: { key: 'developers.takesRaw' }, does: 'developers.epChunkPut' },
      { method: 'GET', path: '/api/bridges/:id/messages/:messageId/chunks/:index', takes: { key: 'developers.takesOwnerOrClaim' }, does: 'developers.epChunkGet' },
      { method: 'DELETE', path: '/api/bridges/:id/messages/:messageId', takes: { key: 'developers.takesOwner' }, does: 'developers.epRemove' },
    ],
  },
  {
    id: 'connect',
    label: 'developers.groupConnect',
    items: [
      { method: 'POST', path: '/api/bridges/:id/connect-codes', takes: { key: 'developers.takesOwnerBody', params: { body: '{ ownerPublicKey }' } }, does: 'developers.epCodeStart' },
      { method: 'POST', path: '/api/connect', takes: { code: '{ code, publicKey, device? }' }, does: 'developers.epConnect' },
      { method: 'POST', path: '/api/bridges/:id/connect-codes/:sessionId/key', takes: { key: 'developers.takesOwnerBody', params: { body: '{ wrappedKey }' } }, does: 'developers.epKey' },
      { method: 'GET', path: '/api/connect/:sessionId', takes: { key: 'developers.takesPoll' }, does: 'developers.epPoll' },
    ],
  },
];

const ERROR_CODES = ['bad_request', 'unauthorized', 'forbidden', 'not_found', 'code_invalid', 'expired', 'view_limit', 'conflict', 'payload_too_large', 'rate_limited', 'nearby_full', 'storage_full', 'internal'] as const;

type EmbedTheme = 'auto' | 'light' | 'dark';
const EMBED_THEMES: readonly { id: EmbedTheme; label: MessageKey }[] = [
  { id: 'auto', label: 'developers.themeAuto' },
  { id: 'light', label: 'theme.light' },
  { id: 'dark', label: 'theme.dark' },
];

/** The two lines a site pastes to show Bridge: the frame, and the helper that sizes it to the tool (public/embed.js). */
export function embedSnippet(base: string, theme: EmbedTheme): string {
  const query = theme === 'auto' ? '' : `?theme=${theme}`;
  return `<iframe src="${base}/embed${query}" title="Passwork Bridge" allow="clipboard-write" style="width:100%;max-width:1064px;height:640px;border:0"></iframe>
<script src="${base}/embed.js" async></script>`;
}

/** The three promises, in the panel beside the tool. */
const GLANCE: readonly { readonly title: MessageKey; readonly text: MessageKey }[] = [
  { title: 'developers.glance1Title', text: 'developers.glance1Text' },
  { title: 'developers.glance2Title', text: 'developers.glance2Text' },
  { title: 'developers.glance3Title', text: 'developers.glance3Text' },
];

/**
 * For developers: an opening with the whole idea in one terminal beside it, then open sections rather than cards —
 * the quick start (three calls, then a runnable sample), the embed with a preview of how it sits on a page, the
 * endpoints in three groups, and how it stays private with the error codes.
 */
export function DevelopersPage(): React.JSX.Element {
  const { code: language } = useLocale();
  const toast = useToast();
  const [lang, setLang] = useState<Lang>('curl');
  const [group, setGroup] = useState<string>('bridges');
  const [copied, setCopied] = useState(false);
  const [embedTheme, setEmbedTheme] = useState<EmbedTheme>('auto');
  const [embedCopied, setEmbedCopied] = useState(false);
  const base = origin();
  const snippet = embedSnippet(base, embedTheme);

  const copyEmbed = async (): Promise<void> => {
    const ok = await copyText(snippet);
    if (ok) {
      setEmbedCopied(true);
      window.setTimeout(() => {
        setEmbedCopied(false);
      }, 1800);
    } else toast.error(t('developers.copyCodeFailed'));
  };

  useEffect(() => {
    document.title = t('developers.docTitle');
  }, [language]);

  const copy = async (): Promise<void> => {
    const ok = await copyText(SAMPLES[lang](base));
    if (ok) {
      setCopied(true);
      window.setTimeout(() => {
        setCopied(false);
      }, 1800);
    } else toast.error(t('developers.copySampleFailed'));
  };

  const codeTag = { code: (children: React.ReactNode) => <code>{children}</code> };
  const endpoints = ENDPOINT_GROUPS.find((item) => item.id === group) ?? ENDPOINT_GROUPS[0];

  return (
    <Page>
      <section className={styles.hero}>
        <div className={styles.heroCopy}>
          <p className={styles.eyebrow}>{t('nav.developers')}</p>
          <h1 className={styles.title}>{t('developers.title')}</h1>
          <p className={styles.lead}>{t('developers.lead')}</p>
          <div className={styles.actions}>
            <Link to="/" className={ui.btn} data-testid="back-to-app">
              {t('common.tryBridge')}
              <ArrowRightIcon size={16} />
            </Link>
            <a href={README_URL} target="_blank" rel="noreferrer" className={[ui.btn, ui.secondary].join(' ')}>
              {t('developers.docs')}
              <ArrowUpRightIcon size={16} />
            </a>
          </div>
          <ul className={styles.principles} aria-label={t('developers.principles')}>
            {GLANCE.map((item, index) => (
              <li key={item.title} className={styles.principle}>
                <span className={styles.principleIcon} aria-hidden="true">
                  {index === 0 ? <ShieldCheckIcon size={16} /> : index === 1 ? <LinkIcon size={16} /> : <PlugIcon size={16} />}
                </span>
                <span>
                  <strong>{t(item.title)}.</strong> {t(item.text)}
                </span>
              </li>
            ))}
          </ul>
        </div>
        {/* the whole idea in one terminal: create, post the ciphertext, share the link with the key after # */}
        <figure className={styles.terminal} aria-label={t('developers.terminalLabel')}>
          <span className={styles.terminalBar} aria-hidden="true">
            <i />
            <i />
            <i />
            <span>bash</span>
          </span>
          <pre className={styles.terminalCode}>
            <span className={styles.prompt}>$</span> BRIDGE={base}
            {'\n\n'}
            <span className={styles.comment}># 1 · create a bridge: one view, ten minutes</span>
            {'\n'}
            <span className={styles.prompt}>$</span> curl -X POST $BRIDGE/api/bridges \{'\n'}
            {'    '}-H {"'content-type: application/json'"} \{'\n'}
            {'    '}-d {"'{\"ttlSeconds\": 600, \"maxViews\": 1}'"}
            {'\n'}
            <span className={styles.output}>{'{ "bridgeId": "K7mN9pQr…", "ownerToken": "…", … }'}</span>
            {'\n\n'}
            <span className={styles.comment}># 2 · post what you encrypted on your side</span>
            {'\n'}
            <span className={styles.prompt}>$</span> curl -X POST $BRIDGE/api/bridges/$ID/messages \{'\n'}
            {'    '}-H {'"authorization: Bearer $OWNER_TOKEN"'} \{'\n'}
            {'    '}-H {"'content-type: application/json'"} \{'\n'}
            {'    '}-d {"'{\"kind\": \"text\", \"body\": \"…\"}'"}
            {'\n\n'}
            <span className={styles.comment}># 3 · share the link; the key stays after #</span>
            {'\n'}
            <span className={styles.link}>$BRIDGE/b/K7mN9pQr…#key</span>
          </pre>
        </figure>
      </section>

      <section className={styles.section} aria-labelledby="start-title">
        <header className={styles.sectionHead}>
          <p className={styles.sectionEyebrow}>{t('developers.quickStart')}</p>
          <h2 id="start-title" className={styles.sectionTitle}>
            {t('developers.apiReference')}
          </h2>
          <p className={styles.sectionLead}>{t('developers.startLead')}</p>
        </header>
        <ol className={styles.flow}>
          <li>
            <span className={styles.flowTitle}>{t('developers.flow1')}</span>
            <code>POST /api/bridges</code>
          </li>
          <li>
            <span className={styles.flowTitle}>{t('developers.flow2')}</span>
            <code>POST /api/bridges/:id/messages</code>
          </li>
          <li>
            <span className={styles.flowTitle}>{t('developers.flow3')}</span>
            <code>/b/:id#key</code>
          </li>
        </ol>
        <div className={styles.codebox}>
          <div className={styles.codeHead}>
            <div role="tablist" aria-label={t('developers.samplesLabel')} className={styles.langs}>
              {LANGS.map((item) => (
                <button
                  key={item.id}
                  type="button"
                  role="tab"
                  id={`tab-${item.id}`}
                  aria-selected={lang === item.id}
                  aria-controls="sample"
                  className={styles.lang}
                  onClick={() => {
                    setLang(item.id);
                  }}
                >
                  <span className={ui.segTabLabel} data-label={item.label}>
                    {item.label}
                  </span>
                </button>
              ))}
            </div>
            <button type="button" className={styles.copy} onClick={() => void copy()} aria-label={t('developers.copySample')}>
              {copied ? <CheckIcon size={15} /> : <CopyIcon size={15} />}
              {copied ? t('common.copied') : t('common.copy')}
            </button>
          </div>
          <pre id="sample" role="tabpanel" aria-labelledby={`tab-${lang}`} className={styles.code}>
            {SAMPLES[lang](base)
              .split('\n')
              .map((line, index) => (
                <span key={index} className={line.startsWith('#') || line.startsWith('//') ? styles.comment : undefined}>
                  {line}
                  {'\n'}
                </span>
              ))}
          </pre>
        </div>
      </section>

      <section className={[styles.section, styles.embed].join(' ')} aria-labelledby="embed-title">
        <div className={styles.embedCopy}>
          <p className={styles.sectionEyebrow}>{t('developers.embedEyebrow')}</p>
          <h2 id="embed-title" className={styles.sectionTitle}>
            {t('developers.embedTitle')}
          </h2>
          <p className={styles.sectionLead}>{t('developers.embedLead')}</p>
          <div className={[ui.seg, styles.embedThemes].join(' ')} role="radiogroup" aria-label={t('developers.embedColours')}>
            {EMBED_THEMES.map((item) => (
              <button
                key={item.id}
                type="button"
                role="radio"
                aria-checked={embedTheme === item.id}
                className={ui.segTab}
                onClick={() => {
                  setEmbedTheme(item.id);
                }}
              >
                <span className={ui.segTabLabel} data-label={t(item.label)}>
                  {t(item.label)}
                </span>
              </button>
            ))}
          </div>
          <div className={styles.actions}>
            <button type="button" className={ui.btn} onClick={() => void copyEmbed()} data-testid="copy-embed">
              {embedCopied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
              {embedCopied ? t('common.copied') : t('developers.copyCode')}
            </button>
          </div>
        </div>
        <div className={styles.embedShow}>
          {/* how it looks on a page: a browser with the tool in it, in the colours chosen */}
          <figure className={styles.browser} data-theme-preview={embedTheme}>
            <span className={styles.browserBar} aria-hidden="true">
              <i />
              <i />
              <i />
              <span className={styles.browserAddress}>your-site.com/support</span>
            </span>
            <img className={styles.previewLight} src={embedLight} alt={t('developers.embedAlt')} loading="lazy" decoding="async" />
            <img className={styles.previewDark} src={embedDark} alt="" aria-hidden="true" loading="lazy" decoding="async" />
          </figure>
          <div className={styles.codebox}>
            <pre className={styles.code} aria-label={t('developers.embedCode')} data-testid="embed-code">
              {snippet}
            </pre>
          </div>
        </div>
      </section>

      <section className={styles.section} aria-labelledby="endpoints-title">
        <div className={styles.sectionHeadRow}>
          <div>
            <p className={styles.sectionEyebrow}>{t('developers.reference')}</p>
            <h2 id="endpoints-title" className={styles.sectionTitle}>
            {t('developers.endpoints')}
            </h2>
          </div>
          <div className={styles.groups} role="group" aria-label={t('developers.endpointGroups')}>
            {ENDPOINT_GROUPS.map((item) => (
              <button
                key={item.id}
                type="button"
                className={styles.chip}
                aria-pressed={group === item.id}
                onClick={() => {
                  setGroup(item.id);
                }}
              >
                <span className={ui.segTabLabel} data-label={t(item.label)}>
                  {t(item.label)}
                </span>
                <span className={styles.chipCount}>{item.items.length}</span>
              </button>
            ))}
          </div>
        </div>
        <ul className={styles.endpoints} aria-label={t('developers.endpointsOf', { group: endpoints ? t(endpoints.label) : '' })}>
          {endpoints?.items.map(({ method, path, takes, does, doesParams }) => (
            <li key={`${method} ${path}`} className={styles.endpoint}>
              <span className={styles.method} data-method={method}>
                {method}
              </span>
              <code className={styles.path}>{path}</code>
              <span className={styles.does}>{t(does, doesParams)}</span>
              <span className={styles.takes}>{'code' in takes ? takes.code : t(takes.key, takes.params)}</span>
            </li>
          ))}
        </ul>
      </section>

      <section className={styles.section} aria-labelledby="private-title">
        <p className={styles.sectionEyebrow}>{t('developers.security')}</p>
        <h2 id="private-title" className={styles.sectionTitle}>
          {t('developers.private')}
        </h2>
        <ol className={styles.steps}>
          <li>{tr('developers.step1', codeTag)}</li>
          <li>{tr('developers.step2', codeTag)}</li>
          <li>{t('developers.step3')}</li>
          <li>{t('developers.step4')}</li>
        </ol>
        <div className={styles.errors}>
          <p className={styles.errorsTitle}>{tr('developers.errors', codeTag, { shape: '{ "error": code, "message": text }' })}</p>
          <ul className={styles.codes} aria-label={t('developers.errorCodes')}>
            {ERROR_CODES.map((code) => (
              <li key={code}>
                <code>{code}</code>
              </li>
            ))}
          </ul>
        </div>
      </section>
    </Page>
  );
}
